import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  InputNumber,
  Space,
  Modal,
  Form,
  Select,
  Switch,
  message,
  Tag,
  Typography,
  Card,
  Popconfirm,
  Tooltip,
  Tabs,
  Layout,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  KeyOutlined,
  SearchOutlined,
  LogoutOutlined,
  TeamOutlined,
  UserOutlined,
  CopyOutlined,
  FileSearchOutlined,
  ClearOutlined,
  SendOutlined,
  MoonOutlined,
  SunOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ConfigProvider, theme as antdTheme } from 'antd';
import './AdminPortal.css';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Header, Content } = Layout;

// Helper function to format date as dd/mm/yyyy
// JavaScript's Date constructor automatically handles timezone conversion
const formatDate = (date: string): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to format date and time as dd/mm/yyyy HH:mm
// Converts UTC time from database to user's local timezone
// JavaScript's Date automatically converts timezone-aware strings to local time
const formatDateTime = (date: string): string => {
  const d = new Date(date);
  // getDate(), getMonth(), getHours(), etc. automatically use local timezone
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// Helper function to derive unified subscription status
const getSubscriptionStatus = (company: Company): 'Active' | 'Expired' | 'Disabled' => {
  if (company.is_disabled) return 'Disabled';
  if (company.subscription_expires_at && new Date(company.subscription_expires_at) < new Date()) return 'Expired';
  return 'Active';
};

// Helper function to get status color for tags
const getStatusColor = (status: 'Active' | 'Expired' | 'Disabled'): string => {
  switch (status) {
    case 'Active': return 'green';
    case 'Expired': return 'red';
    case 'Disabled': return 'orange';
  }
};

// Helper function to get plan default seat limit
const getPlanDefaultSeats = (plan: string): number => {
  switch (plan) {
    case 'Single User': return 1;
    case 'Company (Single Site)': return 5;
    case 'Company (Global)': return 20;
    default: return 1;
  }
};

// Helper function to format seats display with override indicator
const formatSeatsDisplay = (company: Company): string => {
  const used = company.user_count || 0;
  const limit = company.seat_limit;
  const defaultLimit = getPlanDefaultSeats(company.subscription_plan);
  
  // If unlimited (null or undefined)
  if (!limit) {
    return `${used} / Unlimited`;
  }
  
  // If limit equals default, show without override indicator
  if (limit === defaultLimit) {
    return `${used} / ${limit}`;
  }
  
  // Calculate override difference
  const override = limit - defaultLimit;
  const overrideIndicator = override > 0 ? ` (+${override})` : ` (${override})`;
  
  return `${used} / ${limit}${overrideIndicator}`;
};

// Helper function to compute package from access areas
// Normalizes area names (trim whitespace, case-insensitive) and applies rules:
// - Trial: only "Skin Cancer - Trial Version" (and no other access areas)
// - Focus: exactly one Focus area AND does not include parent domain
// - In Sight: includes all areas for a domain (e.g., Dermatology + Skin Cancer + Psoriasis)
// - Custom: all other combinations
const computePackage = (accessAreas: string[]): 'Trial' | 'Focus' | 'In Sight' | 'Custom' => {
  if (!accessAreas || accessAreas.length === 0) {
    return 'Custom';
  }

  // Normalize access areas (trim whitespace, lowercase for comparison)
  const normalized = accessAreas.map(area => area.trim().toLowerCase());
  
  // Dermatology domain
  const hasDermatology = normalized.includes('dermatology');
  const hasSkinCancer = normalized.includes('skin cancer');
  const hasSkinCancerTrial = normalized.includes('skin cancer - trial version');
  const hasPsoriasis = normalized.includes('psoriasis');

  // Ophthalmology domain
  const hasOphthalmology = normalized.includes('ophthalmology');
  const hasRetinalDisorders = normalized.includes('retinal disorders');
  const hasGlaucoma = normalized.includes('glaucoma');

  // Cell, Gene & RNA domain (single umbrella TA in v1)
  const hasCellGeneRna = normalized.includes('cell, gene & rna therapies');

  // Trial: only "Skin Cancer - Trial Version" (and no other access areas)
  if (normalized.length === 1 && hasSkinCancerTrial) {
    return 'Trial';
  }

  // In Sight: includes all areas for Dermatology domain
  if (hasDermatology && hasSkinCancer && hasPsoriasis) {
    return 'In Sight';
  }

  // In Sight: includes all areas for Ophthalmology domain
  if (hasOphthalmology && hasRetinalDisorders && hasGlaucoma) {
    return 'In Sight';
  }

  // In Sight: Cell, Gene & RNA domain currently has just one umbrella TA. Treat exactly that single
  // CGT TA as the In Sight package. (Future Focus children will be added later.)
  if (
    hasCellGeneRna &&
    !hasDermatology && !hasSkinCancer && !hasPsoriasis &&
    !hasOphthalmology && !hasRetinalDisorders && !hasGlaucoma &&
    !hasSkinCancerTrial &&
    normalized.length === 1
  ) {
    return 'In Sight';
  }

  // Focus: exactly one Focus area (Dermatology domain)
  if (!hasDermatology) {
    const hasOnlySkinCancer = hasSkinCancer && !hasPsoriasis;
    const hasOnlyPsoriasis = hasPsoriasis && !hasSkinCancer;
    if (hasOnlySkinCancer || hasOnlyPsoriasis) {
      return 'Focus';
    }
  }

  // Focus: exactly one Focus area (Ophthalmology domain)
  if (!hasOphthalmology) {
    const hasOnlyRetinal = hasRetinalDisorders && !hasGlaucoma;
    const hasOnlyGlaucoma = hasGlaucoma && !hasRetinalDisorders;
    if (hasOnlyRetinal || hasOnlyGlaucoma) {
      return 'Focus';
    }
  }

  // Custom: all other combinations
  return 'Custom';
};

interface User {
  id: number;
  email: string;
  company_id?: number;
  company_name?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
  // Company-level fields (shown for reference)
  subscription_plan?: string;
  seat_limit?: number;
  subscription_expires_at?: string;
  company_is_disabled?: boolean;  // Company disable status
  licensed_therapeutic_areas: string[];
}

interface Company {
  id: number;
  name: string;
  subscription_plan: string;
  seat_limit?: number;
  subscription_expires_at?: string;
  is_disabled: boolean;
  disabled_at?: string;
  disabled_reason?: string;
  subscription_starts_at?: string;
  created_at: string;
  updated_at: string;
  licensed_therapeutic_areas: string[];
  user_count: number;
}

interface WaitlistEntry {
  id: number;
  email: string;
  marketing_opt_in: boolean;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  referrer_url?: string;
  landing_path: string;
  session_id: string;
  user_agent?: string;
  created_at: string;
  updated_at: string;
  status: 'pending' | 'approved' | 'assigned' | 'rejected';
  processed_at?: string;
  processed_by?: number;
  assigned_company_id?: number;
  created_user_id?: number;
  created_company_id?: number;
  admin_notes?: string;
}

interface PurchaseRequest {
  id: number;
  stripe_session_id: string;
  stripe_payment_intent_id?: string;
  stripe_customer_id?: string;
  customer_email: string;
  plan: 'focus' | 'in-sight' | 'panorama';
  tier: string;
  seats: number;
  amount_paid: number;
  currency: string;
  access_areas: string[];
  payment_status: string;
  status: 'pending' | 'approved' | 'assigned' | 'rejected';
  processed_at?: string;
  processed_by?: number;
  assigned_company_id?: number;
  created_user_id?: number;
  created_company_id?: number;
  admin_notes?: string;
  created_at: string;
  updated_at: string;
}

const AdminPortal: React.FC = () => {
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  // Tab persistence
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('admin_active_tab') || 'users';
  });

  // Update sessionStorage when tab changes
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    sessionStorage.setItem('admin_active_tab', key);
  };

  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isCreateUserModalVisible, setIsCreateUserModalVisible] = useState(false);
  const [isEditUserModalVisible, setIsEditUserModalVisible] = useState(false);
  const [isCreateCompanyModalVisible, setIsCreateCompanyModalVisible] = useState(false);
  const [isEditCompanyModalVisible, setIsEditCompanyModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [therapeuticAreas, setTherapeuticAreas] = useState<string[]>([]);
  const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
  const [createUserForm] = Form.useForm();
  const [editUserForm] = Form.useForm();
  const [createCompanyForm] = Form.useForm();
  const [editCompanyForm] = Form.useForm();
  const [resettingPasswordUserId, setResettingPasswordUserId] = useState<number | null>(null);
  const [passwordResetModalVisible, setPasswordResetModalVisible] = useState(false);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [passwordResetUserEmail, setPasswordResetUserEmail] = useState<string>('');
  const [createUserPasswordModalVisible, setCreateUserPasswordModalVisible] = useState(false);
  const [createUserTempPassword, setCreateUserTempPassword] = useState<string>('');
  const [createUserEmail, setCreateUserEmail] = useState<string>('');
  const [waitlistEntries, setWaitlistEntries] = useState<WaitlistEntry[]>([]);
  const [approvePasswordModalVisible, setApprovePasswordModalVisible] = useState(false);
  const [approveTempPassword, setApproveTempPassword] = useState<string>('');
  const [approveUserEmail, setApproveUserEmail] = useState<string>('');
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistStatusFilter, setWaitlistStatusFilter] = useState<'pending' | 'all' | 'rejected' | 'approved' | 'assigned'>('pending');
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedWaitlistEntry, setSelectedWaitlistEntry] = useState<WaitlistEntry | null>(null);
  const [approveForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [rejectForm] = Form.useForm();
  const [processingAction, setProcessingAction] = useState(false);
  const [existingCompanyName, setExistingCompanyName] = useState<string | null>(null);
  const [userTableKey, setUserTableKey] = useState(0);
  const [companyTableKey, setCompanyTableKey] = useState(0);
  const [disableCompanyModalVisible, setDisableCompanyModalVisible] = useState(false);
  const [companyToDisable, setCompanyToDisable] = useState<Company | null>(null);
  const [disableReason, setDisableReason] = useState<string>('');
  // Purchase Requests state
  const [purchaseRequests, setPurchaseRequests] = useState<PurchaseRequest[]>([]);
  const [purchaseRequestsLoading, setPurchaseRequestsLoading] = useState(false);
  const [purchaseRequestsStatusFilter, setPurchaseRequestsStatusFilter] = useState<'pending' | 'all' | 'rejected' | 'approved' | 'assigned'>('pending');
  const [purchaseRequestApproveModalVisible, setPurchaseRequestApproveModalVisible] = useState(false);
  const [purchaseRequestAssignModalVisible, setPurchaseRequestAssignModalVisible] = useState(false);
  const [purchaseRequestRejectModalVisible, setPurchaseRequestRejectModalVisible] = useState(false);
  const [selectedPurchaseRequest, setSelectedPurchaseRequest] = useState<PurchaseRequest | null>(null);
  const [purchaseRequestApproveForm] = Form.useForm();
  const [purchaseRequestAssignForm] = Form.useForm();
  const [purchaseRequestRejectForm] = Form.useForm();

  // Fetch users
  const fetchUsers = async (search?: string) => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/users', {
        params: search ? { search } : {}
      });
      setUsers(response.data);
    } catch (error: any) {
      message.error('Failed to load users: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies
  const fetchCompanies = async (search?: string) => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/companies', {
        params: search ? { search } : {}
      });
      setCompanies(response.data);
      setAvailableCompanies(response.data); // For dropdown
    } catch (error: any) {
      message.error('Failed to load companies: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies for dropdown (without loading state)
  const fetchCompaniesForDropdown = async () => {
    try {
      const response = await api.get('/api/admin/companies');
      setAvailableCompanies(response.data);
    } catch (error: any) {
      console.error('Failed to load companies for dropdown:', error);
    }
  };

  // Fetch therapeutic areas
  const fetchTherapeuticAreas = async () => {
    try {
      const response = await api.get('/api/admin/therapeutic-areas');
      setTherapeuticAreas(response.data);
    } catch (error: any) {
      console.error('Failed to load therapeutic areas:', error);
    }
  };

  const fetchWaitlist = async (search?: string, status?: string) => {
    setWaitlistLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (status && status !== 'all') params.status_filter = status;
      else if (!status && waitlistStatusFilter !== 'all') params.status_filter = waitlistStatusFilter;
      
      const response = await api.get('/api/admin/waitlist', { params });
      setWaitlistEntries(response.data);
    } catch (error: any) {
      message.error('Failed to load trial requests: ' + (error.response?.data?.detail || error.message));
    } finally {
      setWaitlistLoading(false);
    }
  };

  const fetchPurchaseRequests = async (search?: string, status?: string) => {
    setPurchaseRequestsLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      // Always send status_filter, even if 'all' - backend will handle it
      const filterToUse = status || purchaseRequestsStatusFilter;
      if (filterToUse) {
        params.status_filter = filterToUse;
      }
      
      const response = await api.get('/api/admin/purchase-requests', { params });
      setPurchaseRequests(response.data);
    } catch (error: any) {
      message.error('Failed to load purchase requests: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPurchaseRequestsLoading(false);
    }
  };

  useEffect(() => {
    fetchTherapeuticAreas();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers(searchText);
    } else if (activeTab === 'companies') {
      fetchCompanies(searchText);
    } else if (activeTab === 'trial-requests') {
      fetchWaitlist(searchText);
    } else if (activeTab === 'purchase-requests') {
      fetchPurchaseRequests(searchText);
    }
  }, [activeTab]);

  // Handle search
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (activeTab === 'users') {
      fetchUsers(value);
    } else if (activeTab === 'companies') {
      fetchCompanies(value);
    } else if (activeTab === 'trial-requests') {
      fetchWaitlist(value, waitlistStatusFilter);
    } else if (activeTab === 'purchase-requests') {
      fetchPurchaseRequests(value, purchaseRequestsStatusFilter);
    }
  };

  // Reset all filters for User Management table
  const handleResetUserFilters = () => {
    setUserTableKey(prev => prev + 1);
    message.success('All filters reset');
  };

  // Reset all filters for Company Management table
  const handleResetCompanyFilters = () => {
    setCompanyTableKey(prev => prev + 1);
    message.success('All filters reset');
  };

  // =====================================================
  // USER MANAGEMENT HANDLERS
  // =====================================================

  // Handle create user
  const handleCreateUser = async (values: any) => {
    try {
      // Build payload matching backend schema: AdminUserCreate (password is optional, will be auto-generated)
      const payload: any = {
        email: values.email,
        role: values.role || 'customer',
        is_active: values.is_active !== undefined ? values.is_active : true,
        company_id: values.company_id
      };

      const response = await api.post('/api/admin/users', payload);
      
      // Check if a temporary password was generated
      if (response.data.temporary_password) {
        const tempPasswordValue = response.data.temporary_password;
        const userEmail = response.data.user.email;
        
        // Show password modal
        setCreateUserTempPassword(tempPasswordValue);
        setCreateUserEmail(userEmail);
        setCreateUserPasswordModalVisible(true);
      } else {
        message.success('User created successfully');
      }
      
      setIsCreateUserModalVisible(false);
      createUserForm.resetFields();
      fetchUsers(searchText);
      fetchCompanies(); // Refresh companies to update user counts
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message;
      
      // Check if it's an email already exists error
      if (errorMessage && (errorMessage.includes('Email already') || errorMessage.includes('already registered'))) {
        createUserForm.setFields([
          {
            name: 'email',
            errors: [errorMessage]
          }
        ]);
      } else {
        message.error('Failed to create user: ' + errorMessage);
      }
    }
  };

  // Handle edit user
  const handleEditUser = (user: User) => {
    setEditingUser(user);
    editUserForm.setFieldsValue({
      email: user.email,
      company_id: user.company_id,
      is_active: user.is_active
    });
    setIsEditUserModalVisible(true);
  };

  // Handle update user
  const handleUpdateUser = async (values: any) => {
    if (!editingUser) return;
    
    try {
      // Build payload matching backend schema: AdminUserUpdate
      const payload: any = {};
      
      if (values.email !== undefined) {
        payload.email = values.email;
      }
      if (values.company_id !== undefined) {
        payload.company_id = values.company_id || null; // Allow clearing company
      }
      // Prevent updating is_active for admin users
      if (values.is_active !== undefined && editingUser.role !== 'admin') {
        payload.is_active = values.is_active;
      }

      await api.patch(`/api/admin/users/${editingUser.id}`, payload);
      message.success('User updated successfully');
      setIsEditUserModalVisible(false);
      setEditingUser(null);
      editUserForm.resetFields();
      fetchUsers(searchText);
      fetchCompanies(); // Refresh companies to update user counts
    } catch (error: any) {
      message.error('Failed to update user: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle toggle active
  const handleToggleActive = async (user: User) => {
    // Prevent disabling admin users
    if (user.role === 'admin') {
      message.error('Cannot disable admin users');
      return;
    }
    try {
      await api.post(`/api/admin/users/${user.id}/toggle-active`);
      message.success(`User ${user.is_active ? 'disabled' : 'enabled'} successfully`);
      fetchUsers(searchText);
    } catch (error: any) {
      message.error('Failed to toggle user status: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle reset password
  const handleResetPassword = async (user: User) => {
    setResettingPasswordUserId(user.id);
    try {
      console.log(`[AdminPortal] Attempting to reset password for user ${user.id} (${user.email})`);
      const url = `/api/admin/users/${user.id}/reset-password`;
      console.log(`[AdminPortal] Making POST request to: ${url}`);
      console.log(`[AdminPortal] API base URL:`, api.defaults.baseURL);
      console.log(`[AdminPortal] Auth token present:`, !!localStorage.getItem('auth_token'));
      
      const response = await api.post(`/api/admin/users/${user.id}/reset-password`);
      
      if (!response.data || !response.data.temporary_password) {
        throw new Error('Invalid response from server: missing temporary_password');
      }
      
      const tempPasswordValue = response.data.temporary_password;
      
      // Set state to show modal
      setTempPassword(tempPasswordValue);
      setPasswordResetUserEmail(user.email);
      setPasswordResetModalVisible(true);
    } catch (error: any) {
      console.error(`[AdminPortal] Password reset failed for user ${user.id}:`, error);
      console.error(`[AdminPortal] Error details:`, {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers
      });
      
      // Check for specific error types
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        message.error('Request timed out. Please try again.');
      } else if (error.response?.status === 401) {
        message.error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        message.error('You do not have permission to reset passwords.');
      } else if (error.response?.status === 404) {
        message.error('User not found or endpoint does not exist.');
      } else if (error.response?.status >= 500) {
        message.error('Server error. Please try again later.');
      } else {
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error occurred';
        message.error(`Failed to reset password: ${errorMessage}`);
      }
    } finally {
      setResettingPasswordUserId(null);
    }
  };

  // =====================================================
  // COMPANY MANAGEMENT HANDLERS
  // =====================================================

  // Handle create company
  const handleCreateCompany = async (values: any) => {
    try {
      // Build payload matching backend schema: CompanyCreate
      const payload: any = {
        name: values.name,
        subscription_plan: values.subscription_plan,
        licensed_therapeutic_areas: values.licensed_therapeutic_areas || [],
        is_disabled: false  // New companies are created as enabled by default
      };

      // Convert seat_limit to integer if provided
      if (values.seat_limit !== undefined && values.seat_limit !== null) {
        payload.seat_limit = Number(values.seat_limit);
      }

      // Handle datetime fields
      if (values.subscription_starts_at) {
        payload.subscription_starts_at = values.subscription_starts_at.toISOString();
      }
      if (values.subscription_expires_at) {
        payload.subscription_expires_at = values.subscription_expires_at.toISOString();
      }

      await api.post('/api/admin/companies', payload);
      message.success('Company created successfully');
      setIsCreateCompanyModalVisible(false);
      createCompanyForm.resetFields();
      fetchCompanies(searchText);
      fetchUsers(); // Refresh users to show updated company info
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message;
      
      // Check if it's a company name already exists error
      if (errorMessage && errorMessage.includes('Company name already')) {
        createCompanyForm.setFields([
          {
            name: 'name',
            errors: [errorMessage]
          }
        ]);
      } else {
        message.error('Failed to create company: ' + errorMessage);
      }
    }
  };

  // Handle edit company
  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    
    editCompanyForm.setFieldsValue({
      name: company.name,
      subscription_plan: company.subscription_plan,
      seat_limit: company.seat_limit,
      licensed_therapeutic_areas: company.licensed_therapeutic_areas,
      subscription_starts_at: company.subscription_starts_at ? dayjs(company.subscription_starts_at) : null,
      subscription_expires_at: company.subscription_expires_at ? dayjs(company.subscription_expires_at) : null
    });
    setIsEditCompanyModalVisible(true);
  };

  // Handle update company
  const handleUpdateCompany = async (values: any) => {
    if (!editingCompany) return;
    
    try {
      // Build payload matching backend schema: CompanyUpdate
      const payload: any = {};
      
      if (values.name !== undefined) {
        payload.name = values.name;
      }
      if (values.subscription_plan !== undefined) {
        payload.subscription_plan = values.subscription_plan;
      }
      if (values.licensed_therapeutic_areas !== undefined) {
        payload.licensed_therapeutic_areas = values.licensed_therapeutic_areas;
      }
      
      // Convert seat_limit to integer if provided
      if (values.seat_limit !== undefined && values.seat_limit !== null) {
        payload.seat_limit = Number(values.seat_limit);
      } else if (values.seat_limit === null || values.seat_limit === '') {
        payload.seat_limit = null;
      }

      
      // Handle datetime fields
      if (values.subscription_starts_at !== undefined) {
        payload.subscription_starts_at = values.subscription_starts_at ? 
          values.subscription_starts_at.toISOString() : null;
      }
      if (values.subscription_expires_at !== undefined) {
        payload.subscription_expires_at = values.subscription_expires_at ? 
          values.subscription_expires_at.toISOString() : null;
      }

      await api.patch(`/api/admin/companies/${editingCompany.id}`, payload);
      message.success('Company updated successfully');
      setIsEditCompanyModalVisible(false);
      setEditingCompany(null);
      editCompanyForm.resetFields();
      fetchCompanies(searchText);
      fetchUsers(); // Refresh users to show updated company info
    } catch (error: any) {
      message.error('Failed to update company: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle disable company with reason
  const handleDisableCompany = async (company: Company, reason?: string) => {
    try {
      await api.post(`/api/admin/companies/${company.id}/disable`, {
        reason: reason || undefined
      });
      
      message.success(`Company disabled successfully`);
      fetchCompanies(searchText);
    } catch (error: any) {
      message.error('Failed to disable company: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Handle enable company
  const handleEnableCompany = async (company: Company) => {
    try {
      await api.post(`/api/admin/companies/${company.id}/enable`);
      
      message.success(`Company enabled successfully`);
      fetchCompanies(searchText);
    } catch (error: any) {
      message.error('Failed to enable company: ' + (error.response?.data?.detail || error.message));
    }
  };

  // User table columns
  const userColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a: User, b: User) => a.email.localeCompare(b.email),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: (a: User, b: User) => {
        // Admins first, then Users
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        return 0;
      },
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Admin', value: 'admin' },
        { text: 'User', value: 'user' },
      ],
      onFilter: (value: any, record: User) => {
        if (value === 'all') return true;
        if (value === 'admin') return record.role === 'admin';
        if (value === 'user') return record.role !== 'admin';
        return true;
      },
      render: (role: string) => {
        const isAdmin = role === 'admin';
        return (
          <Tag 
            color={isAdmin ? 'purple' : 'default'}
            style={{
              fontSize: '11px',
              padding: '2px 8px',
              borderRadius: '12px',
              fontWeight: 500,
              background: isAdmin ? '#f6f0ff' : '#f5f5f5',
              border: isAdmin ? '1px solid #d3adf7' : '1px solid #d9d9d9',
              color: isAdmin ? '#722ed1' : '#595959',
              margin: 0
            }}
          >
            {isAdmin ? 'Admin' : 'User'}
          </Tag>
        );
      },
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      sorter: (a: User, b: User) => {
        const nameA = a.company_name || '';
        const nameB = b.company_name || '';
        return nameA.localeCompare(nameB);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const uniqueCompanies = Array.from(new Set(users.map(u => u.company_name).filter(Boolean))).sort();
        return (
          <div style={{ padding: 8 }}>
            <Select
              showSearch
              placeholder="Search company"
              value={selectedKeys[0]}
              onChange={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
              onClear={() => {
                clearFilters();
                confirm();
              }}
              style={{ width: 200 }}
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={[
                { label: 'All Companies', value: null },
                ...uniqueCompanies.map(company => ({ label: company, value: company }))
              ]}
            />
          </div>
        );
      },
      onFilter: (value: any, record: User) => {
        if (!value) return true;
        return record.company_name === value;
      },
      render: (text: string) => text || <span style={{ color: '#999' }}>No company</span>,
    },
    {
      title: 'Plan',
      key: 'package',
      sorter: (a: User, b: User) => {
        const packageA = computePackage(a.licensed_therapeutic_areas || []);
        const packageB = computePackage(b.licensed_therapeutic_areas || []);
        return packageA.localeCompare(packageB);
      },
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Trial', value: 'Trial' },
        { text: 'Focus', value: 'Focus' },
        { text: 'In Sight', value: 'In Sight' },
        { text: 'Custom', value: 'Custom' },
      ],
      onFilter: (value: any, record: User) => {
        if (value === 'all') return true;
        const packageValue = computePackage(record.licensed_therapeutic_areas || []);
        return packageValue === value;
      },
      render: (_: any, record: User) => {
        const packageValue = computePackage(record.licensed_therapeutic_areas || []);
        const isCustom = packageValue === 'Custom';
        const colors: Record<string, string> = {
          'Trial': 'orange',
          'Focus': 'cyan',
          'In Sight': 'blue',
          'Custom': 'default'
        };
        return (
          <Tooltip title={isCustom ? "Access areas don't match standard packages." : undefined}>
            <Tag color={colors[packageValue] || 'default'}>
              {packageValue}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: () => (
        <Tooltip title="Inherited from company">
          License
        </Tooltip>
      ),
      dataIndex: 'subscription_plan',
      key: 'subscription_plan',
      sorter: (a: User, b: User) => {
        const order: Record<string, number> = {
          'Single User': 1,
          'Company (Single Site)': 2,
          'Company (Global)': 3
        };
        const planA = a.subscription_plan || '';
        const planB = b.subscription_plan || '';
        return (order[planA] || 999) - (order[planB] || 999);
      },
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Single User', value: 'Single User' },
        { text: 'Company (Single Site)', value: 'Company (Single Site)' },
        { text: 'Company (Global)', value: 'Company (Global)' },
      ],
      onFilter: (value: any, record: User) => {
        if (value === 'all') return true;
        return record.subscription_plan === value;
      },
      render: (plan: string) => {
        if (!plan) return '-';
        const colors: Record<string, string> = {
          'Single User': 'blue',
          'Company (Single Site)': 'green',
          'Company (Global)': 'purple'
        };
        return <Tag color={colors[plan] || 'default'}>{plan}</Tag>;
      },
    },
    {
      title: () => (
        <Tooltip title="Inherited from company">
          Access Areas
        </Tooltip>
      ),
      dataIndex: 'licensed_therapeutic_areas',
      key: 'licensed_therapeutic_areas',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const allAreas = Array.from(new Set(
          users.flatMap(u => u.licensed_therapeutic_areas || [])
        )).sort();
        return (
          <div style={{ padding: 8 }}>
            <Select
              mode="multiple"
              placeholder="Select access areas"
              value={selectedKeys}
              onChange={(values) => {
                setSelectedKeys(values);
                confirm();
              }}
              onClear={() => {
                clearFilters();
                confirm();
              }}
              style={{ width: 250 }}
              allowClear
              options={allAreas.map(area => ({ label: area, value: area }))}
            />
            <div style={{ marginTop: 8 }}>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  clearFilters();
                  confirm();
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        );
      },
      onFilter: (value: any, record: User) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        const selectedAreas = Array.isArray(value) ? value : [value];
        const userAreas = record.licensed_therapeutic_areas || [];
        return selectedAreas.some((area: string) => userAreas.includes(area));
      },
      render: (areas: string[]) => (
        <div style={{ maxWidth: '200px' }}>
          {areas && areas.length > 0 ? (
            areas.map(area => <Tag key={area} style={{ marginBottom: '4px' }}>{area}</Tag>)
          ) : (
            <span style={{ color: '#999' }}>None</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      sorter: (a: User, b: User) => {
        // Helper to get effective status
        const getEffectiveStatus = (user: User): boolean => {
          if (user.role === 'admin') return true;  // Admins always active
          if (user.company_is_disabled) return false;  // Company disabled blocks user
          if (user.subscription_expires_at && new Date(user.subscription_expires_at) < new Date()) return false;  // Expired blocks user
          return user.is_active;  // User-level status
        };
        const statusA = getEffectiveStatus(a);
        const statusB = getEffectiveStatus(b);
        return statusA === statusB ? 0 : statusA ? -1 : 1;
      },
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Active', value: 'active' },
        { text: 'Disabled', value: 'disabled' },
      ],
      onFilter: (value: any, record: User) => {
        if (value === 'all') return true;
        // Helper to get effective status
        const getEffectiveStatus = (user: User): boolean => {
          if (user.role === 'admin') return true;
          if (user.company_is_disabled) return false;
          if (user.subscription_expires_at && new Date(user.subscription_expires_at) < new Date()) return false;
          return user.is_active;
        };
        const status = getEffectiveStatus(record);
        if (value === 'active') return status === true;
        if (value === 'disabled') return status === false;
        return true;
      },
      render: (isActive: boolean, record: User) => {
        // Helper to get effective status and reason
        const getEffectiveStatus = (user: User): { status: boolean; reason?: string } => {
          if (user.role === 'admin') return { status: true };
          if (user.company_is_disabled) return { status: false, reason: 'Company Disabled' };
          if (user.subscription_expires_at && new Date(user.subscription_expires_at) < new Date()) {
            return { status: false, reason: 'Company Expired' };
          }
          return { status: user.is_active, reason: user.is_active ? undefined : 'User Disabled' };
        };
        
        const effective = getEffectiveStatus(record);
        const tooltipTitle = effective.reason ? `Disabled: ${effective.reason}` : undefined;
        
        return (
          <Tooltip title={tooltipTitle}>
            <Tag color={effective.status ? 'success' : 'error'}>
              {effective.status ? 'Active' : 'Disabled'}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a: User, b: User) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateA - dateB;
      },
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login',
      key: 'last_login',
      sorter: (a: User, b: User) => {
        // "Never" (null/empty) sorts last
        if (!a.last_login && !b.last_login) return 0;
        if (!a.last_login) return 1; // a is "Never", sort last
        if (!b.last_login) return -1; // b is "Never", sort last
        const dateA = new Date(a.last_login).getTime();
        const dateB = new Date(b.last_login).getTime();
        return dateB - dateA; // Most recent first
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        
        return (
          <div style={{ padding: 8 }}>
            <Select
              placeholder="Filter by last login"
              value={selectedKeys[0]}
              onChange={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
              onClear={() => {
                clearFilters();
                confirm();
              }}
              style={{ width: 200 }}
              allowClear
              options={[
                { label: 'All', value: 'all' },
                { label: 'Never', value: 'never' },
                { label: 'Last 7 days', value: 'last7days' },
                { label: 'Last 30 days', value: 'last30days' },
              ]}
            />
          </div>
        );
      },
      onFilter: (value: any, record: User) => {
        if (!value || value === 'all') return true;
        
        if (value === 'never') {
          return !record.last_login;
        }
        
        if (!record.last_login) return false;
        
        const lastLogin = new Date(record.last_login);
        const now = new Date();
        
        if (value === 'last7days') {
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return lastLogin >= sevenDaysAgo;
        }
        
        if (value === 'last30days') {
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return lastLogin >= thirtyDaysAgo;
        }
        
        return true;
      },
      render: (date: string) => date ? formatDateTime(date) : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: any, record: User) => (
        <Space size="small">
          <Tooltip title="Edit User">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            />
          </Tooltip>
          {record.role !== 'admin' && (() => {
            // Check if company is disabled or expired
            const isCompanyDisabled = record.company_is_disabled === true;
            const isCompanyExpired = !!(record.subscription_expires_at && 
              new Date(record.subscription_expires_at) < new Date());
            const isCompanyBlocked = Boolean(isCompanyDisabled || isCompanyExpired);
            
            // Determine tooltip message
            const tooltipMessage = isCompanyBlocked 
              ? (isCompanyDisabled 
                  ? 'Company is disabled; change company status to restore access.'
                  : 'Company subscription expired; change company status to restore access.')
              : (record.is_active ? 'Disable User' : 'Enable User');
            
            const button = (
              <Button
                type="link"
                icon={record.is_active ? <LockOutlined /> : <UnlockOutlined />}
                danger={record.is_active}
                disabled={isCompanyBlocked}
                style={isCompanyBlocked ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              />
            );
            
            if (isCompanyBlocked) {
              return (
                <Tooltip title={tooltipMessage}>
                  {button}
                </Tooltip>
              );
            }
            
            return (
              <Tooltip title={tooltipMessage}>
                <Popconfirm
                  title={`Are you sure you want to ${record.is_active ? 'disable' : 'enable'} this user?`}
                  onConfirm={() => handleToggleActive(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  {button}
                </Popconfirm>
              </Tooltip>
            );
          })()}
          <Tooltip title="Reset Password">
            <Popconfirm
              title="Reset this user's password? A temporary password will be generated."
              onConfirm={async () => {
                try {
                  await handleResetPassword(record);
                } catch (error) {
                  console.error('[AdminPortal] Error in Popconfirm onConfirm:', error);
                  message.error('An error occurred while resetting the password.');
                }
              }}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ loading: resettingPasswordUserId === record.id }}
            >
              <Button
                type="link"
                icon={<KeyOutlined />}
                loading={resettingPasswordUserId === record.id}
                disabled={resettingPasswordUserId === record.id}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Waitlist action handlers
  const handleApprove = (entry: WaitlistEntry) => {
    setSelectedWaitlistEntry(entry);
    approveForm.resetFields();
    approveForm.setFieldsValue({
      subscription_plan: 'Single User',
      trial_days: 30,
      licensed_therapeutic_areas: []
    });
    setExistingCompanyName(null);
    setApproveModalVisible(true);
  };

  const handleAssign = (entry: WaitlistEntry) => {
    setSelectedWaitlistEntry(entry);
    assignForm.resetFields();
    setAssignModalVisible(true);
  };

  const handleReject = (entry: WaitlistEntry) => {
    setSelectedWaitlistEntry(entry);
    rejectForm.resetFields();
    setRejectModalVisible(true);
  };

  const handleApproveSubmit = async (values: any) => {
    if (!selectedWaitlistEntry) return;
    
    setProcessingAction(true);
    try {
      const response = await api.post(`/api/admin/waitlist/${selectedWaitlistEntry.id}/approve`, values);
      
      const tempPasswordValue = response.data.temporary_password;
      const userEmail = response.data.user.email;
      
      // Show password modal
      setApproveTempPassword(tempPasswordValue);
      setApproveUserEmail(userEmail);
      setApprovePasswordModalVisible(true);
      
      // Close approve modal
      setApproveModalVisible(false);
      setSelectedWaitlistEntry(null);
      approveForm.resetFields();
      setExistingCompanyName(null);
      
      // Refresh data
      fetchWaitlist(searchText, waitlistStatusFilter);
      fetchUsers(); // Refresh users list
      fetchCompanies(); // Refresh companies list
    } catch (error: any) {
      message.error('Failed to approve request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleAssignSubmit = async (values: any) => {
    if (!selectedWaitlistEntry) return;
    
    setProcessingAction(true);
    try {
      const response = await api.post(`/api/admin/waitlist/${selectedWaitlistEntry.id}/assign`, values);
      
      const tempPasswordValue = response.data.temporary_password;
      const userEmail = response.data.user.email;
      
      // Show password modal
      setApproveTempPassword(tempPasswordValue);
      setApproveUserEmail(userEmail);
      setApprovePasswordModalVisible(true);
      
      // Close assign modal
      setAssignModalVisible(false);
      setSelectedWaitlistEntry(null);
      assignForm.resetFields();
      
      // Refresh data
      fetchWaitlist(searchText, waitlistStatusFilter);
      fetchUsers(); // Refresh users list
      fetchCompanies(); // Refresh companies list
    } catch (error: any) {
      message.error('Failed to assign request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectSubmit = async (values: any) => {
    if (!selectedWaitlistEntry) return;
    
    setProcessingAction(true);
    try {
      await api.post(`/api/admin/waitlist/${selectedWaitlistEntry.id}/reject`, values);
      message.success('Trial request rejected.');
      setRejectModalVisible(false);
      setSelectedWaitlistEntry(null);
      rejectForm.resetFields();
      fetchWaitlist(searchText, waitlistStatusFilter);
    } catch (error: any) {
      message.error('Failed to reject request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingAction(false);
    }
  };

  // Purchase Request action handlers
  const handlePurchaseRequestApprove = (request: PurchaseRequest) => {
    setSelectedPurchaseRequest(request);
    purchaseRequestApproveForm.resetFields();
    // Pre-fill form with purchase request data
    purchaseRequestApproveForm.setFieldsValue({
      subscription_plan: request.tier,
      licensed_therapeutic_areas: request.access_areas || [],
      seat_limit: request.seats,
      subscription_days: 365, // Annual subscription
    });
    setExistingCompanyName(null);
    setPurchaseRequestApproveModalVisible(true);
  };

  const handlePurchaseRequestAssign = (request: PurchaseRequest) => {
    setSelectedPurchaseRequest(request);
    purchaseRequestAssignForm.resetFields();
    setPurchaseRequestAssignModalVisible(true);
  };

  const handlePurchaseRequestReject = (request: PurchaseRequest) => {
    setSelectedPurchaseRequest(request);
    purchaseRequestRejectForm.resetFields();
    setPurchaseRequestRejectModalVisible(true);
  };

  const handlePurchaseRequestApproveSubmit = async (values: any) => {
    if (!selectedPurchaseRequest) return;
    
    setProcessingAction(true);
    try {
      const response = await api.post(`/api/admin/purchase-requests/${selectedPurchaseRequest.id}/approve`, values);
      
      const tempPasswordValue = response.data.temporary_password;
      const userEmail = response.data.user.email;
      
      // Show password modal
      setApproveTempPassword(tempPasswordValue);
      setApproveUserEmail(userEmail);
      setApprovePasswordModalVisible(true);
      
      // Close approve modal
      setPurchaseRequestApproveModalVisible(false);
      setSelectedPurchaseRequest(null);
      purchaseRequestApproveForm.resetFields();
      setExistingCompanyName(null);
      
      // Refresh data
      fetchPurchaseRequests(searchText, purchaseRequestsStatusFilter);
      fetchUsers(); // Refresh users list
      fetchCompanies(); // Refresh companies list
    } catch (error: any) {
      message.error('Failed to approve request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingAction(false);
    }
  };

  const handlePurchaseRequestAssignSubmit = async (values: any) => {
    if (!selectedPurchaseRequest) return;
    
    setProcessingAction(true);
    try {
      const response = await api.post(`/api/admin/purchase-requests/${selectedPurchaseRequest.id}/assign`, values);
      
      const tempPasswordValue = response.data.temporary_password;
      const userEmail = response.data.user.email;
      
      // Show password modal
      setApproveTempPassword(tempPasswordValue);
      setApproveUserEmail(userEmail);
      setApprovePasswordModalVisible(true);
      
      // Close assign modal
      setPurchaseRequestAssignModalVisible(false);
      setSelectedPurchaseRequest(null);
      purchaseRequestAssignForm.resetFields();
      
      // Refresh data
      fetchPurchaseRequests(searchText, purchaseRequestsStatusFilter);
      fetchUsers(); // Refresh users list
      fetchCompanies(); // Refresh companies list
    } catch (error: any) {
      message.error('Failed to assign request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingAction(false);
    }
  };

  const handlePurchaseRequestRejectSubmit = async (values: any) => {
    if (!selectedPurchaseRequest) return;
    
    setProcessingAction(true);
    try {
      await api.post(`/api/admin/purchase-requests/${selectedPurchaseRequest.id}/reject`, values);
      message.success('Purchase request rejected. Remember to issue refund via Stripe Dashboard if needed.');
      setPurchaseRequestRejectModalVisible(false);
      setSelectedPurchaseRequest(null);
      purchaseRequestRejectForm.resetFields();
      fetchPurchaseRequests(searchText, purchaseRequestsStatusFilter);
    } catch (error: any) {
      message.error('Failed to reject request: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingAction(false);
    }
  };

  const waitlistColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a: WaitlistEntry, b: WaitlistEntry) => a.email.localeCompare(b.email),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          'pending': 'orange',
          'approved': 'green',
          'assigned': 'blue',
          'rejected': 'red'
        };
        const labels: Record<string, string> = {
          'pending': 'Pending',
          'approved': 'Approved',
          'assigned': 'Assigned',
          'rejected': 'Rejected'
        };
        return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>;
      },
    },
    {
      title: 'Signed Up',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a: WaitlistEntry, b: WaitlistEntry) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      render: (date: string) => formatDateTime(date)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_: any, record: WaitlistEntry) => {
        const isPending = record.status === 'pending';
        const isRejected = record.status === 'rejected';
        
        // Show actions for pending or rejected requests (allow changing decisions)
        if (isPending || isRejected) {
          return (
            <Space size="small">
              <Tooltip title={isRejected ? "Approve this previously rejected request" : "Approve and create company + user"}>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleApprove(record)}
                >
                  Approve
                </Button>
              </Tooltip>
              <Tooltip title={isRejected ? "Assign this previously rejected request" : "Assign to existing company"}>
                <Button
                  size="small"
                  onClick={() => handleAssign(record)}
                >
                  Assign
                </Button>
              </Tooltip>
              {isPending && (
                <Tooltip title="Reject request">
                  <Popconfirm
                    title="Reject this trial request?"
                    onConfirm={() => handleReject(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      danger
                      size="small"
                    >
                      Reject
                    </Button>
                  </Popconfirm>
                </Tooltip>
              )}
              {isRejected && (
                <Tooltip title="This request was previously rejected">
                  <Tag color="red">Rejected</Tag>
                </Tooltip>
              )}
            </Space>
          );
        }
        
        // For approved/assigned, show status only
        return (
          <span style={{ color: '#999' }}>
            {record.status === 'approved' && <Tag color="green">Approved</Tag>}
            {record.status === 'assigned' && <Tag color="blue">Assigned</Tag>}
          </span>
        );
      },
    }
  ];

  const purchaseRequestColumns = [
    {
      title: 'Email',
      dataIndex: 'customer_email',
      key: 'customer_email',
      sorter: (a: PurchaseRequest, b: PurchaseRequest) => a.customer_email.localeCompare(b.customer_email),
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan: string) => {
        const planLabels: Record<string, string> = {
          'focus': 'Focus',
          'in-sight': 'In Sight',
          'panorama': 'Panorama'
        };
        return <Tag color="blue">{planLabels[plan] || plan}</Tag>;
      },
    },
    {
      title: 'License Type',
      dataIndex: 'tier',
      key: 'tier',
    },
    {
      title: 'Access Areas',
      dataIndex: 'access_areas',
      key: 'access_areas',
      render: (areas: string[]) => areas && areas.length > 0 ? areas.join(', ') : 'N/A',
    },
    {
      title: 'Amount',
      dataIndex: 'amount_paid',
      key: 'amount_paid',
      render: (amount: number, record: PurchaseRequest) => {
        const dollars = (amount / 100).toFixed(2);
        return `$${dollars} ${record.currency || 'USD'}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          'pending': 'orange',
          'approved': 'green',
          'assigned': 'blue',
          'rejected': 'red'
        };
        const labels: Record<string, string> = {
          'pending': 'Pending',
          'approved': 'Approved',
          'assigned': 'Assigned',
          'rejected': 'Rejected'
        };
        return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>;
      },
    },
    {
      title: 'Purchased',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a: PurchaseRequest, b: PurchaseRequest) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      render: (date: string) => formatDateTime(date)
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_: any, record: PurchaseRequest) => {
        const isPending = record.status === 'pending';
        const isRejected = record.status === 'rejected';
        
        // Show actions for pending or rejected requests
        if (isPending || isRejected) {
          return (
            <Space size="small">
              <Tooltip title={isRejected ? "Approve this previously rejected request" : "Approve and create company + user"}>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handlePurchaseRequestApprove(record)}
                >
                  Approve
                </Button>
              </Tooltip>
              <Tooltip title={isRejected ? "Assign this previously rejected request" : "Assign to existing company"}>
                <Button
                  size="small"
                  onClick={() => handlePurchaseRequestAssign(record)}
                >
                  Assign
                </Button>
              </Tooltip>
              {isPending && (
                <Tooltip title="Reject request">
                  <Popconfirm
                    title="Reject this purchase request?"
                    onConfirm={() => handlePurchaseRequestReject(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      danger
                      size="small"
                    >
                      Reject
                    </Button>
                  </Popconfirm>
                </Tooltip>
              )}
              {isRejected && (
                <Tooltip title="This request was previously rejected">
                  <Tag color="red">Rejected</Tag>
                </Tooltip>
              )}
            </Space>
          );
        }
        
        // For approved/assigned, show status only
        return <Tag color={record.status === 'approved' ? 'green' : 'blue'}>{record.status === 'approved' ? 'Approved' : 'Assigned'}</Tag>;
      }
    },
  ];

  // Company table columns
  const companyColumns = [
    {
      title: 'Company Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Company, b: Company) => a.name.localeCompare(b.name),
    },
    {
      title: 'Plan',
      key: 'package',
      sorter: (a: Company, b: Company) => {
        const packageA = computePackage(a.licensed_therapeutic_areas);
        const packageB = computePackage(b.licensed_therapeutic_areas);
        return packageA.localeCompare(packageB);
      },
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Trial', value: 'Trial' },
        { text: 'Focus', value: 'Focus' },
        { text: 'In Sight', value: 'In Sight' },
        { text: 'Custom', value: 'Custom' },
      ],
      onFilter: (value: any, record: Company) => {
        if (value === 'all') return true;
        const packageValue = computePackage(record.licensed_therapeutic_areas);
        return packageValue === value;
      },
      render: (_: any, record: Company) => {
        const packageValue = computePackage(record.licensed_therapeutic_areas);
        const isCustom = packageValue === 'Custom';
        const colors: Record<string, string> = {
          'Trial': 'orange',
          'Focus': 'cyan',
          'In Sight': 'blue',
          'Custom': 'default'
        };
        return (
          <Tooltip title={isCustom ? "Access areas don't match standard packages." : undefined}>
            <Tag color={colors[packageValue] || 'default'}>
              {packageValue}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'License',
      dataIndex: 'subscription_plan',
      key: 'subscription_plan',
      sorter: (a: Company, b: Company) => {
        const order: Record<string, number> = {
          'Single User': 1,
          'Company (Single Site)': 2,
          'Company (Global)': 3
        };
        const planA = a.subscription_plan || '';
        const planB = b.subscription_plan || '';
        return (order[planA] || 999) - (order[planB] || 999);
      },
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Single User', value: 'Single User' },
        { text: 'Company (Single Site)', value: 'Company (Single Site)' },
        { text: 'Company (Global)', value: 'Company (Global)' },
      ],
      onFilter: (value: any, record: Company) => {
        if (value === 'all') return true;
        return record.subscription_plan === value;
      },
      render: (plan: string) => {
        const colors: Record<string, string> = {
          'Single User': 'blue',
          'Company (Single Site)': 'green',
          'Company (Global)': 'purple'
        };
        return <Tag color={colors[plan] || 'default'}>{plan}</Tag>;
      },
    },
    {
      title: () => (
        <Tooltip title="Controls what data users from this company can access">
          Access Areas
        </Tooltip>
      ),
      dataIndex: 'licensed_therapeutic_areas',
      key: 'licensed_therapeutic_areas',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
        const allAreas = Array.from(new Set(
          companies.flatMap(c => c.licensed_therapeutic_areas || [])
        )).sort();
        return (
          <div style={{ padding: 8 }}>
            <Select
              mode="multiple"
              placeholder="Select access areas"
              value={selectedKeys}
              onChange={(values) => {
                setSelectedKeys(values);
                confirm();
              }}
              onClear={() => {
                clearFilters();
                confirm();
              }}
              style={{ width: 250 }}
              allowClear
              options={allAreas.map(area => ({ label: area, value: area }))}
            />
            <div style={{ marginTop: 8 }}>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  clearFilters();
                  confirm();
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        );
      },
      onFilter: (value: any, record: Company) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        const selectedAreas = Array.isArray(value) ? value : [value];
        const companyAreas = record.licensed_therapeutic_areas || [];
        return selectedAreas.some((area: string) => companyAreas.includes(area));
      },
      render: (areas: string[]) => (
        <div style={{ maxWidth: '200px' }}>
          {areas && areas.length > 0 ? (
            areas.map(area => <Tag key={area} style={{ marginBottom: '4px' }}>{area}</Tag>)
          ) : (
            <span style={{ color: '#999' }}>None</span>
          )}
        </div>
      ),
    },
    {
      title: 'Seats',
      key: 'seats',
      sorter: (a: Company, b: Company) => {
        // Sort by seat_limit first (null/unlimited last), then by used count
        const limitA = a.seat_limit ?? Number.MAX_SAFE_INTEGER;
        const limitB = b.seat_limit ?? Number.MAX_SAFE_INTEGER;
        
        if (limitA !== limitB) {
          return limitA - limitB;
        }
        
        // If limits are equal, sort by used count
        const usedA = a.user_count || 0;
        const usedB = b.user_count || 0;
        return usedA - usedB;
      },
      render: (_: any, record: Company) => {
        return formatSeatsDisplay(record);
      },
    },
    {
      title: 'Subscription Status',
      key: 'subscription_status',
      sorter: (a: Company, b: Company) => {
        const statusA = getSubscriptionStatus(a);
        const statusB = getSubscriptionStatus(b);
        // Sort order: Disabled -> Expired -> Active
        const order = { 'Disabled': 0, 'Expired': 1, 'Active': 2 };
        return order[statusA] - order[statusB];
      },
      filters: [
        { text: 'All', value: 'all' },
        { text: 'Active', value: 'Active' },
        { text: 'Expired', value: 'Expired' },
        { text: 'Disabled', value: 'Disabled' },
      ],
      onFilter: (value: any, record: Company) => {
        if (value === 'all') return true;
        const status = getSubscriptionStatus(record);
        return status === value;
      },
      render: (_: any, record: Company) => {
        const status = getSubscriptionStatus(record);
        const color = getStatusColor(status);
        return (
          <Tag color={color}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Start Date',
      dataIndex: 'subscription_starts_at',
      key: 'subscription_starts_at',
      sorter: (a: Company, b: Company) => {
        const dateA = a.subscription_starts_at ? new Date(a.subscription_starts_at).getTime() : 0;
        const dateB = b.subscription_starts_at ? new Date(b.subscription_starts_at).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: string) => date ? formatDateTime(date) : '—',
    },
    {
      title: 'Renewal / Expiry',
      dataIndex: 'subscription_expires_at',
      key: 'subscription_expires_at',
      sorter: (a: Company, b: Company) => {
        const dateA = a.subscription_expires_at ? new Date(a.subscription_expires_at).getTime() : 0;
        const dateB = b.subscription_expires_at ? new Date(b.subscription_expires_at).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: string) => date ? formatDateTime(date) : '—',
    },
    {
      title: 'Days Remaining',
      key: 'days_remaining',
      sorter: (a: Company, b: Company) => {
        const getDaysRemaining = (company: Company) => {
          if (!company.subscription_expires_at) return Number.MAX_SAFE_INTEGER;
          const diff = new Date(company.subscription_expires_at).getTime() - new Date().getTime();
          return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        };
        return getDaysRemaining(a) - getDaysRemaining(b);
      },
      render: (_: any, record: Company) => {
        if (!record.subscription_expires_at) return '—';
        const diff = new Date(record.subscription_expires_at).getTime() - new Date().getTime();
        const days = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        return days;
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a: Company, b: Company) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateA - dateB;
      },
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_: any, record: Company) => (
        <Space size="small">
          <Tooltip title="Edit Company">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditCompany(record)}
            />
          </Tooltip>
          {record.is_disabled ? (
            <Tooltip title="Enable Company">
              <Popconfirm
                title={`Are you sure you want to enable ${record.name}?`}
                onConfirm={() => handleEnableCompany(record)}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  type="link"
                  icon={<UnlockOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          ) : (
            <Tooltip title="Disable Company">
              <Button
                type="link"
                icon={<LockOutlined />}
                danger
                onClick={() => {
                  setCompanyToDisable(record);
                  setDisableReason('');
                  setDisableCompanyModalVisible(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorBgContainer: isDark ? '#141414' : '#ffffff',
          colorBgElevated: isDark ? '#1f1f1f' : '#ffffff',
          colorBgLayout: isDark ? '#000000' : '#f5f7fa',
          colorBorder: isDark ? '#434343' : '#d9d9d9',
          colorText: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
          colorTextSecondary: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
        },
      }}
    >
      <div className="admin-portal">
        <Layout className="admin-layout">
        <Header className="admin-header">
          <div className="admin-logo-section">
            <img 
              src={process.env.PUBLIC_URL + '/sns-focus-logo.png'} 
              alt="SNS Intelligence" 
            />
            <Title level={3} className="admin-title">Admin Center</Title>
          </div>
          <Space size="middle">
            <Button
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={toggleTheme}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? 'Light' : 'Dark'}
            </Button>
            <Button
              onClick={() => navigate('/app')}
              type="primary"
              className="dashboard-btn"
            >
              Open Portal
            </Button>
            <Button
              onClick={() => navigate('/outreach')}
              type="primary"
              icon={<SendOutlined />}
              className="dashboard-btn"
            >
              Outreach
            </Button>
            <Button
              icon={<LogoutOutlined />}
              onClick={logout}
              danger
            >
              Logout
            </Button>
          </Space>
        </Header>

        <Content className="admin-content">
          <Card className="admin-card">
            <Tabs activeKey={activeTab} onChange={handleTabChange} size="large">
              <TabPane tab={<span><UserOutlined />User Management</span>} key="users">
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text type="secondary">
                    Manage system users, their company associations, and account status.
                  </Typography.Text>
                  <Space>
                    <Input
                      placeholder="Search users..."
                      prefix={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                      style={{ width: 250 }}
                      allowClear
                    />
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleResetUserFilters}
                      size="large"
                    >
                      Reset Filters
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsCreateUserModalVisible(true)}
                      size="large"
                    >
                      Create New User
                    </Button>
                  </Space>
                </div>
                <Table
                  key={userTableKey}
                  columns={userColumns}
                  dataSource={users}
                  loading={loading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} users`,
                  }}
                  className="modern-table"
                />
              </TabPane>

              <TabPane tab={<span><TeamOutlined />Company Management</span>} key="companies">
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text type="secondary">
                    Manage companies, their subscription plans, and therapeutic area licenses.
                  </Typography.Text>
                  <Space>
                    <Input
                      placeholder="Search companies..."
                      prefix={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                      style={{ width: 250 }}
                      allowClear
                    />
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleResetCompanyFilters}
                      size="large"
                    >
                      Reset Filters
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsCreateCompanyModalVisible(true)}
                      size="large"
                    >
                      Create New Company
                    </Button>
                  </Space>
                </div>
                <Table
                  key={companyTableKey}
                  columns={companyColumns}
                  dataSource={companies}
                  loading={loading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} companies`,
                  }}
                  className="modern-table"
                />
              </TabPane>
              <TabPane tab={<span><FileSearchOutlined />Trial Requests</span>} key="trial-requests">
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text type="secondary">
                    Review and process trial requests. Pending requests require action.
                  </Typography.Text>
                  <Space>
                    <Input
                      placeholder="Search requests..."
                      prefix={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                      style={{ width: 250 }}
                      allowClear
                    />
                    <Select
                      value={waitlistStatusFilter}
                      onChange={(value) => {
                        setWaitlistStatusFilter(value);
                        fetchWaitlist(searchText, value);
                      }}
                      style={{ width: 180 }}
                    >
                      <Select.Option value="pending">Pending Only</Select.Option>
                      <Select.Option value="rejected">Rejected</Select.Option>
                      <Select.Option value="approved">Approved</Select.Option>
                      <Select.Option value="assigned">Assigned</Select.Option>
                      <Select.Option value="all">All Requests</Select.Option>
                    </Select>
                  </Space>
                </div>
                <Table
                  columns={waitlistColumns}
                  dataSource={waitlistEntries}
                  loading={waitlistLoading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} ${waitlistStatusFilter === 'pending' ? 'pending' : ''} requests`,
                  }}
                  className="modern-table"
                />
              </TabPane>
              <TabPane tab={<span><FileSearchOutlined />Purchase Requests</span>} key="purchase-requests">
                <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text type="secondary">
                    Review and process purchase requests from completed Stripe payments. Pending requests require action.
                  </Typography.Text>
                  <Space>
                    <Input
                      placeholder="Search requests..."
                      prefix={<SearchOutlined />}
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                      style={{ width: 250 }}
                      allowClear
                    />
                    <Select
                      value={purchaseRequestsStatusFilter}
                      onChange={(value) => {
                        setPurchaseRequestsStatusFilter(value);
                        fetchPurchaseRequests(searchText, value);
                      }}
                      style={{ width: 180 }}
                    >
                      <Select.Option value="pending">Pending Only</Select.Option>
                      <Select.Option value="rejected">Rejected</Select.Option>
                      <Select.Option value="approved">Approved</Select.Option>
                      <Select.Option value="assigned">Assigned</Select.Option>
                      <Select.Option value="all">All Requests</Select.Option>
                    </Select>
                  </Space>
                </div>
                <Table
                  columns={purchaseRequestColumns}
                  dataSource={purchaseRequests}
                  loading={purchaseRequestsLoading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} ${purchaseRequestsStatusFilter === 'pending' ? 'pending' : ''} requests`,
                  }}
                  className="modern-table"
                />
              </TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={isCreateUserModalVisible}
        onCancel={() => {
          setIsCreateUserModalVisible(false);
          createUserForm.resetFields();
        }}
        footer={null}
        width={600}
        afterOpenChange={(open) => {
          if (open) {
            // Fetch companies when modal opens
            fetchCompaniesForDropdown();
          }
        }}
      >
        <Form
          form={createUserForm}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
              {
                validator: async (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  // Check if email already exists
                  try {
                    const response = await api.get('/api/admin/users', {
                      params: { search: value }
                    });
                    const existingUser = response.data.find((u: User) => 
                      u.email.toLowerCase() === value.toLowerCase()
                    );
                    if (existingUser) {
                      return Promise.reject(new Error('This email address already exists'));
                    }
                  } catch (error) {
                    // If search fails, don't block submission - backend will validate
                    console.error('Error checking email:', error);
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="company_id"
            label="Company"
            rules={[
              { required: true, message: 'Please select a company' }
            ]}
          >
            <Select
              placeholder="Select existing company"
              showSearch
              options={availableCompanies.map(company => ({
                label: company.name,
                value: company.id
              }))}
              filterOption={(input, option) => {
                return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>

          <Typography.Text type="secondary" style={{ fontSize: '12px', marginBottom: '16px', display: 'block' }}>
            <strong>Note:</strong> Access (plan & therapeutic areas) is inherited from the company.
          </Typography.Text>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create User
              </Button>
              <Button onClick={() => {
                setIsCreateUserModalVisible(false);
                createUserForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditUserModalVisible}
        onCancel={() => {
          setIsEditUserModalVisible(false);
          setEditingUser(null);
          editUserForm.resetFields();
        }}
        footer={null}
        width={600}
        afterOpenChange={(open) => {
          if (open) {
            // Fetch companies when modal opens
            fetchCompaniesForDropdown();
          }
        }}
      >
        <Form
          form={editUserForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="company_id"
            label="Company"
            tooltip="Change the company this user belongs to"
          >
            <Select
              placeholder="Select company"
              allowClear
              showSearch
              options={availableCompanies.map(company => ({
                label: company.name,
                value: company.id
              }))}
              filterOption={(input, option) => {
                return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update User
              </Button>
              <Button onClick={() => {
                setIsEditUserModalVisible(false);
                setEditingUser(null);
                editUserForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Create Company Modal */}
      <Modal
        title="Create New Company"
        open={isCreateCompanyModalVisible}
        onCancel={() => {
          setIsCreateCompanyModalVisible(false);
          createCompanyForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={createCompanyForm}
          layout="vertical"
          onFinish={handleCreateCompany}
        >
          <Form.Item
            name="name"
            label="Company Name"
            rules={[
              { required: true, message: 'Please enter company name' },
              {
                validator: async (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  // Check if company name already exists
                  try {
                    const response = await api.get('/api/admin/companies', {
                      params: { search: value }
                    });
                    const existingCompany = response.data.find((c: Company) => 
                      c.name.toLowerCase() === value.toLowerCase()
                    );
                    if (existingCompany) {
                      return Promise.reject(new Error('This company name already exists'));
                    }
                  } catch (error) {
                    // If search fails, don't block submission - backend will validate
                    console.error('Error checking company name:', error);
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input placeholder="Company name" />
          </Form.Item>

          <Form.Item
            name="subscription_plan"
            label="License"
            rules={[{ required: true }]}
            initialValue="Single User"
          >
            <Select
              options={[
                { label: 'Single User', value: 'Single User' },
                { label: 'Company (Single Site)', value: 'Company (Single Site)' },
                { label: 'Company (Global)', value: 'Company (Global)' }
              ]}
            />
          </Form.Item>

          <Form.Item
            name="licensed_therapeutic_areas"
            label="Therapeutic Areas"
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) {
                    return Promise.reject(new Error('Please select at least one therapeutic area'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select therapeutic areas"
              style={{ width: '100%' }}
              options={therapeuticAreas.map(area => ({
                label: area,
                value: area
              }))}
            />
          </Form.Item>

          <Form.Item
            name="seat_limit"
            label="Seat Limit (Optional)"
            tooltip="Maximum number of users allowed. Leave empty for unlimited."
          >
            <InputNumber
              placeholder="Enter seat limit"
              min={1}
              style={{ width: '100%' }}
            />
          </Form.Item>


          <Form.Item
            name="subscription_starts_at"
            label="Subscription Start Date"
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Select start date"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="subscription_expires_at"
            label="Subscription Expiry/Renewal Date"
            dependencies={['subscription_starts_at']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue('subscription_starts_at');
                  if (!value || !startDate) {
                    return Promise.resolve();
                  }
                  if (dayjs(value).isAfter(dayjs(startDate)) || dayjs(value).isSame(dayjs(startDate))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Expiry date must be after start date'));
                },
              }),
            ]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Select expiry date"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create Company
              </Button>
              <Button onClick={() => {
                setIsCreateCompanyModalVisible(false);
                createCompanyForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Company Modal */}
      <Modal
        title="Edit Company"
        open={isEditCompanyModalVisible}
        onCancel={() => {
          setIsEditCompanyModalVisible(false);
          setEditingCompany(null);
          editCompanyForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editCompanyForm}
          layout="vertical"
          onFinish={handleUpdateCompany}
        >
          <Form.Item
            name="name"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input placeholder="Company name" />
          </Form.Item>

          <Form.Item
            name="subscription_plan"
            label="License"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { label: 'Single User', value: 'Single User' },
                { label: 'Company (Single Site)', value: 'Company (Single Site)' },
                { label: 'Company (Global)', value: 'Company (Global)' }
              ]}
            />
          </Form.Item>

          <Form.Item
            name="licensed_therapeutic_areas"
            label="Therapeutic Areas"
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) {
                    return Promise.reject(new Error('Please select at least one therapeutic area'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Select therapeutic areas"
              style={{ width: '100%' }}
              options={therapeuticAreas.map(area => ({
                label: area,
                value: area
              }))}
            />
          </Form.Item>

          <Form.Item
            name="seat_limit"
            label="Seat Limit (Optional)"
            tooltip="Maximum number of users allowed. Leave empty for unlimited."
          >
            <InputNumber
              placeholder="Enter seat limit"
              min={1}
              style={{ width: '100%' }}
            />
          </Form.Item>


          <Form.Item
            name="subscription_starts_at"
            label="Subscription Start Date"
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Select start date"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="subscription_expires_at"
            label="Subscription Expiry/Renewal Date"
            dependencies={['subscription_starts_at']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue('subscription_starts_at');
                  if (!value || !startDate) {
                    return Promise.resolve();
                  }
                  if (dayjs(value).isAfter(dayjs(startDate)) || dayjs(value).isSame(dayjs(startDate))) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Expiry date must be after start date'));
                },
              }),
            ]}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Select expiry date"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update Company
              </Button>
              <Button onClick={() => {
                setIsEditCompanyModalVisible(false);
                setEditingCompany(null);
                editCompanyForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Disable Company Modal */}
      <Modal
        title="Disable Company"
        open={disableCompanyModalVisible}
        onCancel={() => {
          setDisableCompanyModalVisible(false);
          setCompanyToDisable(null);
          setDisableReason('');
        }}
        onOk={() => {
          if (companyToDisable) {
            handleDisableCompany(companyToDisable, disableReason);
            setDisableCompanyModalVisible(false);
            setCompanyToDisable(null);
            setDisableReason('');
          }
        }}
        okText="Disable Company"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
      >
        <div style={{ marginBottom: 16 }}>
          <p>
            <strong>⚠️ Warning:</strong> Disabling <strong>{companyToDisable?.name}</strong> will immediately block 
            all <strong>{companyToDisable?.user_count || 0}</strong> users in this company from logging in or using the API.
          </p>
          <p>This action will:</p>
          <ul>
            <li>Prevent all company users from logging in</li>
            <li>Block access to all API endpoints for company users</li>
            <li>Preserve all company data and settings</li>
            <li>Allow re-enabling later without data loss</li>
          </ul>
        </div>
        <div>
          <label htmlFor="disable-reason" style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Reason for disabling (optional):
          </label>
          <Input.TextArea
            id="disable-reason"
            placeholder="Enter reason for disabling this company..."
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
            rows={3}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>

      {/* Approve Waitlist Request Modal */}
      <Modal
        title="Approve Trial Request"
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          setSelectedWaitlistEntry(null);
          approveForm.resetFields();
          setExistingCompanyName(null);
        }}
        footer={null}
        width={700}
        afterOpenChange={(open) => {
          if (open) {
            fetchCompaniesForDropdown();
            fetchTherapeuticAreas();
          }
        }}
      >
        {selectedWaitlistEntry && (
          <Form
            form={approveForm}
            layout="vertical"
            onFinish={handleApproveSubmit}
          >
            <Typography.Text strong style={{ display: 'block', marginBottom: '16px' }}>
              Approving request for: {selectedWaitlistEntry.email}
            </Typography.Text>

            <Form.Item
              name="company_name"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input 
                placeholder="Enter company name"
                onChange={(e) => {
                  const companyName = e.target.value.trim();
                  if (companyName) {
                    const existingCompany = availableCompanies.find(
                      company => company.name.toLowerCase() === companyName.toLowerCase()
                    );
                    if (existingCompany) {
                      setExistingCompanyName(existingCompany.name);
                    } else {
                      setExistingCompanyName(null);
                    }
                  } else {
                    setExistingCompanyName(null);
                  }
                }}
              />
            </Form.Item>
            {existingCompanyName && (
              <Typography.Text type="danger" style={{ display: 'block', marginBottom: '16px', marginTop: '-8px' }}>
                A company named '{existingCompanyName}' already exists.<br />
                This user will be assigned to the existing company.
              </Typography.Text>
            )}

            <Form.Item
              name="subscription_plan"
              label="License"
              rules={[{ required: true }]}
              initialValue="Single User"
            >
              <Select
                options={[
                  { label: 'Single User', value: 'Single User' },
                  { label: 'Company (Single Site)', value: 'Company (Single Site)' },
                  { label: 'Company (Global)', value: 'Company (Global)' }
                ]}
              />
            </Form.Item>

            <Form.Item
              name="licensed_therapeutic_areas"
              label="Therapeutic Areas"
              tooltip="For trial requests, 'Skin Cancer - Trial Version' will be automatically included"
              initialValue={['Skin Cancer - Trial Version']}
            >
              <Select
                mode="multiple"
                placeholder="Select therapeutic areas"
                style={{ width: '100%' }}
                options={therapeuticAreas.map(area => ({
                  label: area,
                  value: area
                }))}
              />
            </Form.Item>

            <Form.Item
              name="seat_limit"
              label="Seat Limit (Optional)"
              tooltip="Maximum number of users allowed. Leave empty for unlimited."
            >
              <InputNumber
                placeholder="Enter seat limit"
                min={1}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="trial_days"
              label="Trial Period (Days)"
              rules={[{ required: true, message: 'Please enter trial period' }]}
              initialValue={30}
            >
              <InputNumber
                placeholder="Trial period in days"
                min={1}
                max={365}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Typography.Text type="warning" style={{ display: 'block', marginBottom: '16px' }}>
              ⚠️ This will create a new company, user account, and generate a temporary password.
            </Typography.Text>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={processingAction}>
                  Approve & Create
                </Button>
                <Button onClick={() => {
                  setApproveModalVisible(false);
                  setSelectedWaitlistEntry(null);
                  approveForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Assign Waitlist Request Modal */}
      <Modal
        title="Assign to Existing Company"
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          setSelectedWaitlistEntry(null);
          assignForm.resetFields();
        }}
        footer={null}
        width={600}
        afterOpenChange={(open) => {
          if (open) {
            fetchCompaniesForDropdown();
          }
        }}
      >
        {selectedWaitlistEntry && (
          <Form
            form={assignForm}
            layout="vertical"
            onFinish={handleAssignSubmit}
          >
            <Typography.Text strong style={{ display: 'block', marginBottom: '16px' }}>
              Assigning request for: {selectedWaitlistEntry.email}
            </Typography.Text>

            <Form.Item
              name="company_id"
              label="Select Company"
              rules={[{ required: true, message: 'Please select a company' }]}
            >
              <Select
                placeholder="Select existing company"
                showSearch
                options={availableCompanies.map(company => ({
                  label: company.name,
                  value: company.id
                }))}
                filterOption={(input, option) => {
                  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
                }}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={processingAction}>
                  Assign to Company
                </Button>
                <Button onClick={() => {
                  setAssignModalVisible(false);
                  setSelectedWaitlistEntry(null);
                  assignForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Reject Waitlist Request Modal */}
      <Modal
        title="Reject Trial Request"
        open={rejectModalVisible}
        onCancel={() => {
          setRejectModalVisible(false);
          setSelectedWaitlistEntry(null);
          rejectForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedWaitlistEntry && (
          <Form
            form={rejectForm}
            layout="vertical"
            onFinish={handleRejectSubmit}
          >
            <Typography.Text strong style={{ display: 'block', marginBottom: '16px' }}>
              Rejecting request for: {selectedWaitlistEntry.email}
            </Typography.Text>

            <Form.Item
              name="notes"
              label="Rejection Reason (Optional)"
            >
              <Input.TextArea rows={4} placeholder="Enter reason for rejection (optional)" />
            </Form.Item>

            <Typography.Text type="danger" style={{ display: 'block', marginBottom: '16px' }}>
              ⚠️ This action cannot be undone. The request will be marked as rejected.
            </Typography.Text>

            <Form.Item>
              <Space>
                <Button danger htmlType="submit" loading={processingAction}>
                  Reject Request
                </Button>
                <Button onClick={() => {
                  setRejectModalVisible(false);
                  setSelectedWaitlistEntry(null);
                  rejectForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Approve Purchase Request Modal */}
      <Modal
        title="Approve Purchase Request"
        open={purchaseRequestApproveModalVisible}
        onCancel={() => {
          setPurchaseRequestApproveModalVisible(false);
          setSelectedPurchaseRequest(null);
          purchaseRequestApproveForm.resetFields();
          setExistingCompanyName(null);
        }}
        footer={null}
        width={600}
        afterOpenChange={(open) => {
          if (open) {
            fetchCompaniesForDropdown();
          }
        }}
      >
        {selectedPurchaseRequest && (
          <Form
            form={purchaseRequestApproveForm}
            layout="vertical"
            onFinish={handlePurchaseRequestApproveSubmit}
          >
            <Typography.Text strong style={{ display: 'block', marginBottom: '16px' }}>
              Approving purchase for: {selectedPurchaseRequest.customer_email}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
              Plan: {selectedPurchaseRequest.plan} | License: {selectedPurchaseRequest.tier} | Amount: ${(selectedPurchaseRequest.amount_paid / 100).toFixed(2)}
            </Typography.Text>

            <Form.Item
              name="company_name"
              label="Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input
                placeholder="Enter company name"
                onChange={async (e) => {
                  const name = e.target.value;
                  if (name) {
                    try {
                      const response = await api.get('/api/admin/companies', {
                        params: { search: name }
                      });
                      const matching = response.data.find((c: Company) => c.name.toLowerCase() === name.toLowerCase());
                      setExistingCompanyName(matching ? matching.name : null);
                    } catch (error) {
                      console.error('Error checking company:', error);
                    }
                  } else {
                    setExistingCompanyName(null);
                  }
                }}
              />
            </Form.Item>
            {existingCompanyName && (
              <Typography.Text type="danger" style={{ display: 'block', marginBottom: '16px', marginTop: '-8px' }}>
                A company named '{existingCompanyName}' already exists.<br />
                This user will be assigned to the existing company.
              </Typography.Text>
            )}

            <Form.Item
              name="subscription_plan"
              label="License"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { label: 'Single User', value: 'Single User' },
                  { label: 'Company (Single Site)', value: 'Company (Single Site)' },
                  { label: 'Company (Global)', value: 'Company (Global)' }
                ]}
              />
            </Form.Item>

            <Form.Item
              name="licensed_therapeutic_areas"
              label="Access Areas"
              tooltip="Defaults to purchased access areas if not specified"
            >
              <Select
                mode="multiple"
                placeholder="Select access areas"
                style={{ width: '100%' }}
                options={therapeuticAreas.map(area => ({
                  label: area,
                  value: area
                }))}
              />
            </Form.Item>

            <Form.Item
              name="seat_limit"
              label="Seat Limit (Optional)"
              tooltip="Maximum number of users allowed. Leave empty for unlimited."
            >
              <InputNumber
                placeholder="Enter seat limit"
                min={1}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="subscription_days"
              label="Subscription Period (Days)"
              rules={[{ required: true, message: 'Please enter subscription period' }]}
              initialValue={365}
            >
              <InputNumber
                placeholder="Subscription period in days"
                min={1}
                max={3650}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="notes"
              label="Notes (Optional)"
            >
              <Input.TextArea rows={3} placeholder="Additional notes (optional)" />
            </Form.Item>

            <Typography.Text type="warning" style={{ display: 'block', marginBottom: '16px' }}>
              ⚠️ This will create a new company, user account, and generate a temporary password.
            </Typography.Text>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={processingAction}>
                  Approve & Create
                </Button>
                <Button onClick={() => {
                  setPurchaseRequestApproveModalVisible(false);
                  setSelectedPurchaseRequest(null);
                  purchaseRequestApproveForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Assign Purchase Request Modal */}
      <Modal
        title="Assign to Existing Company"
        open={purchaseRequestAssignModalVisible}
        onCancel={() => {
          setPurchaseRequestAssignModalVisible(false);
          setSelectedPurchaseRequest(null);
          purchaseRequestAssignForm.resetFields();
        }}
        footer={null}
        width={600}
        afterOpenChange={(open) => {
          if (open) {
            fetchCompaniesForDropdown();
          }
        }}
      >
        {selectedPurchaseRequest && (
          <Form
            form={purchaseRequestAssignForm}
            layout="vertical"
            onFinish={handlePurchaseRequestAssignSubmit}
          >
            <Typography.Text strong style={{ display: 'block', marginBottom: '16px' }}>
              Assigning purchase request for: {selectedPurchaseRequest.customer_email}
            </Typography.Text>

            <Form.Item
              name="company_id"
              label="Select Company"
              rules={[{ required: true, message: 'Please select a company' }]}
            >
              <Select
                placeholder="Select existing company"
                showSearch
                options={availableCompanies.map(company => ({
                  label: company.name,
                  value: company.id
                }))}
                filterOption={(input, option) => {
                  return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
                }}
              />
            </Form.Item>

            <Form.Item
              name="notes"
              label="Notes (Optional)"
            >
              <Input.TextArea rows={3} placeholder="Additional notes (optional)" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={processingAction}>
                  Assign to Company
                </Button>
                <Button onClick={() => {
                  setPurchaseRequestAssignModalVisible(false);
                  setSelectedPurchaseRequest(null);
                  purchaseRequestAssignForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Reject Purchase Request Modal */}
      <Modal
        title="Reject Purchase Request"
        open={purchaseRequestRejectModalVisible}
        onCancel={() => {
          setPurchaseRequestRejectModalVisible(false);
          setSelectedPurchaseRequest(null);
          purchaseRequestRejectForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        {selectedPurchaseRequest && (
          <Form
            form={purchaseRequestRejectForm}
            layout="vertical"
            onFinish={handlePurchaseRequestRejectSubmit}
          >
            <Typography.Text strong style={{ display: 'block', marginBottom: '16px' }}>
              Rejecting purchase request for: {selectedPurchaseRequest.customer_email}
            </Typography.Text>

            <Form.Item
              name="notes"
              label="Rejection Reason (Optional)"
            >
              <Input.TextArea rows={4} placeholder="Enter reason for rejection (optional)" />
            </Form.Item>

            <Typography.Text type="danger" style={{ display: 'block', marginBottom: '16px' }}>
              ⚠️ This action cannot be undone. Remember to issue refund via Stripe Dashboard if needed.
            </Typography.Text>

            <Form.Item>
              <Space>
                <Button danger htmlType="submit" loading={processingAction}>
                  Reject Request
                </Button>
                <Button onClick={() => {
                  setPurchaseRequestRejectModalVisible(false);
                  setSelectedPurchaseRequest(null);
                  purchaseRequestRejectForm.resetFields();
                }}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Approve Success Modal with Temporary Password */}
      <Modal
        title="Trial Request Approved"
        open={approvePasswordModalVisible}
        onCancel={() => {
          setApprovePasswordModalVisible(false);
          setApproveTempPassword('');
          setApproveUserEmail('');
        }}
        footer={[
          <Button key="ok" type="primary" onClick={() => {
            setApprovePasswordModalVisible(false);
            setApproveTempPassword('');
            setApproveUserEmail('');
          }}>
            OK
          </Button>
        ]}
        width={600}
        maskClosable={false}
      >
        <div>
          <Typography.Text strong style={{ display: 'block', marginBottom: '16px' }}>
            ✅ User and company created successfully!
          </Typography.Text>
          
          <p><strong>User Email:</strong> {approveUserEmail}</p>
          
          <p><strong>Temporary Password:</strong></p>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'monospace', 
            fontSize: '20px', 
            fontWeight: 'bold',
            background: '#f0f2f5', 
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '2px solid #1890ff',
            color: '#1890ff'
          }}>
            <span style={{ flex: 1, userSelect: 'all' }}>{approveTempPassword}</span>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={async () => {
                try {
                  // Try modern clipboard API first
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(approveTempPassword);
                    message.success('Password copied to clipboard!');
                  } else {
                    throw new Error('Clipboard API not available');
                  }
                } catch (err) {
                  // Fallback for older browsers or when clipboard API fails
                  const textArea = document.createElement('textarea');
                  textArea.value = approveTempPassword;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-9999px';
                  textArea.style.top = '0';
                  textArea.setAttribute('readonly', '');
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  try {
                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);
                    if (successful) {
                      message.success('Password copied to clipboard!');
                    } else {
                      message.error('Failed to copy password. Please copy manually.');
                    }
                  } catch (fallbackErr) {
                    document.body.removeChild(textArea);
                    message.error('Failed to copy password. Please copy manually.');
                  }
                }
              }}
              size="small"
            >
              Copy
            </Button>
          </div>
          
          <p style={{ color: '#ff4d4f', marginBottom: '12px' }}>
            <strong>Important:</strong> Please securely share this password with the user. 
            They should change it on first login.
          </p>
        </div>
      </Modal>

      {/* Password Reset Success Modal */}
      <Modal
        title="Password Reset Successful"
        open={passwordResetModalVisible}
        onCancel={() => {
          setPasswordResetModalVisible(false);
          setTempPassword('');
          setPasswordResetUserEmail('');
        }}
        footer={[
          <Button key="ok" type="primary" onClick={() => {
            setPasswordResetModalVisible(false);
            setTempPassword('');
            setPasswordResetUserEmail('');
          }}>
            OK
          </Button>
        ]}
        width={600}
        maskClosable={false}
      >
        <div>
          <p><strong>Temporary Password:</strong></p>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'monospace', 
            fontSize: '20px', 
            fontWeight: 'bold',
            background: '#f0f2f5', 
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '2px solid #1890ff',
            color: '#1890ff'
          }}>
            <span style={{ flex: 1, userSelect: 'all' }}>{tempPassword}</span>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={async () => {
                try {
                  // Try modern clipboard API first
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(tempPassword);
                    message.success('Password copied to clipboard!');
                  } else {
                    throw new Error('Clipboard API not available');
                  }
                } catch (err) {
                  // Fallback for older browsers or when clipboard API fails
                  const textArea = document.createElement('textarea');
                  textArea.value = tempPassword;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-9999px';
                  textArea.style.top = '0';
                  textArea.setAttribute('readonly', '');
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  try {
                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);
                    if (successful) {
                      message.success('Password copied to clipboard!');
                    } else {
                      message.error('Failed to copy password. Please copy manually.');
                    }
                  } catch (fallbackErr) {
                    document.body.removeChild(textArea);
                    message.error('Failed to copy password. Please copy manually.');
                  }
                }
              }}
              size="small"
            >
              Copy
            </Button>
          </div>
          <p style={{ color: '#ff4d4f', marginBottom: '12px' }}>
            <strong>Important:</strong> Please securely share this password with the user. 
            They should change it on first login.
          </p>
        </div>
      </Modal>

      {/* Create User Success Modal with Temporary Password */}
      <Modal
        title="User Created Successfully"
        open={createUserPasswordModalVisible}
        onCancel={() => {
          setCreateUserPasswordModalVisible(false);
          setCreateUserTempPassword('');
          setCreateUserEmail('');
        }}
        footer={[
          <Button key="ok" type="primary" onClick={() => {
            setCreateUserPasswordModalVisible(false);
            setCreateUserTempPassword('');
            setCreateUserEmail('');
          }}>
            OK
          </Button>
        ]}
        width={600}
        maskClosable={false}
      >
        <div>
          <p><strong>User Email:</strong> {createUserEmail}</p>
          
          <p><strong>Temporary Password:</strong></p>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontFamily: 'monospace', 
            fontSize: '20px', 
            fontWeight: 'bold',
            background: '#f0f2f5', 
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            border: '2px solid #1890ff',
            color: '#1890ff'
          }}>
            <span style={{ flex: 1, userSelect: 'all' }}>{createUserTempPassword}</span>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={async () => {
                try {
                  // Try modern clipboard API first
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(createUserTempPassword);
                    message.success('Password copied to clipboard!');
                  } else {
                    throw new Error('Clipboard API not available');
                  }
                } catch (err) {
                  // Fallback for older browsers or when clipboard API fails
                  const textArea = document.createElement('textarea');
                  textArea.value = createUserTempPassword;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-9999px';
                  textArea.style.top = '0';
                  textArea.setAttribute('readonly', '');
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  try {
                    const successful = document.execCommand('copy');
                    document.body.removeChild(textArea);
                    if (successful) {
                      message.success('Password copied to clipboard!');
                    } else {
                      message.error('Failed to copy password. Please copy manually.');
                    }
                  } catch (fallbackErr) {
                    document.body.removeChild(textArea);
                    message.error('Failed to copy password. Please copy manually.');
                  }
                }
              }}
              size="small"
            >
              Copy
            </Button>
          </div>
          <p style={{ color: '#ff4d4f', marginBottom: '12px' }}>
            <strong>Important:</strong> Please securely share this password with the user. 
            They should change it on first login.
          </p>
        </div>
      </Modal>
    </div>
    </ConfigProvider>
  );
};

export default AdminPortal;

